import "./index.scss";
import { useEffect, useState } from "react";
import classNames from "classnames";
import debounce from "lodash/debounce";
import { useTranslation } from "react-i18next";
import {
  Checkbox,
  Box,
  Typography,
  DropdownItem,
  Button,
  Icons,
  Tooltip,
  getColor,
  Dropdown,
} from "fds";
import useCheckboxGroup from "../../../shared/hooks/useCheckboxGroup";

import useAppStore from "../../../redux/dispatcher/useAppStore";
import utilsCommon from "../../../shared/utils/common";
import utilsDocs from "../../../shared/utils/docs";
import { DropboxFileInfo } from "../../../types/app";
import dayjs from "dayjs";

interface DropboxListProps {
  onDragStart: (event: any) => void;
  openCreateWorkgroupModal: (type: string) => void;
  handleUploadFilesInUID: (
    selectedList: string[],
    fileList: { id: string; size: number; name: string }[],
    type: "GOOGLE" | "DROPBOX",
    targetFolder?: string
  ) => void;
  changeCurrentPath: (pathId: string) => void;
  currentPath: string;
}
const DropboxList: React.FC<DropboxListProps> = ({
  onDragStart,
  openCreateWorkgroupModal,
  handleUploadFilesInUID,
  changeCurrentPath,
  currentPath,
}) => {
  const { t } = useTranslation();
  const { appState, getDropboxList } = useAppStore();
  const checkboxGroup = useCheckboxGroup<string>();

  const [fileList, changeFileList] = useState<DropboxFileInfo[] | undefined>(
    []
  );
  const [paths, changePaths] = useState([
    { id: "", name: "Dropbox", path: "/" },
  ]);
  const [shownType, changeShownType] = useState("detail"); // detail: 자세히 , simple: 간략히
  const [order, changeSortBy] = useState("datedesc"); // file // 이름 오름차순, filedesc // 이름 내림차순 , date // 수정된 날짜 오름차순, datedesc // 수정된 날짜 내림차순
  const [nextToken, changeNextToken] = useState<string | null | undefined>(
    undefined
  );
  const [buttonClick, setButtonClick] = useState(0);

  useEffect(() => {
    if (nextToken === null) return;
    getDropboxList({
      folder: paths[paths.length - 1].id,
      pageSize: 30,
      pageToken: nextToken,
      order: order,
      isRefresh: false,
      folderPath: currentPath,
    });
  }, [nextToken, currentPath, order]);
  useEffect(() => {
    changeFileList([]);
    changeNextToken(undefined);
    checkboxGroup.handleAllClick([]);
    changeCurrentPath(paths[paths.length - 1].path);
  }, [paths]);
  useEffect(() => {
    changeFileList(
      [...(appState.dropboxFile?.files ?? [])].slice().sort((a, b) => {
        const aIsFolder = a.size === undefined;
        const bIsFolder = b.size === undefined;
        return aIsFolder && !bIsFolder
          ? -1
          : bIsFolder && !aIsFolder
          ? 1
          : order === "datedesc"
          ? dayjs(b.serverModified).diff(a.serverModified)
          : order === "date"
          ? -dayjs(b.serverModified).diff(a.serverModified)
          : order === "file"
          ? a.name > b.name
            ? 1
            : -1
          : a.name > b.name
          ? -1
          : 1;
      })
    );
  }, [appState.dropboxFile]);
  useEffect(() => {
    if (fileList && fileList.length > 0)
      setTimeout(() => {
        if (!(fileList && fileList.length > 0)) return;
        const totalHeight =
          document.getElementById("dropboxFiles")?.clientHeight ?? 0;
        const totalItemHeight =
          (shownType === "detail" ? 68 : 40) * fileList.length;
        if (
          totalHeight > totalItemHeight &&
          appState.dropboxFile?.files &&
          appState.dropboxFile?.files.length === 30
        ) {
          changeNextToken(appState.dropboxFile?.nextPageToken);
        }
      }, 3000);
  }, [fileList, shownType]);

  useEffect(() => {
    const handleResize = debounce(() => {
      if (!(fileList && fileList.length > 0)) return;
      const totalHeight =
        document.getElementById("dropboxFiles")?.clientHeight ?? 0;
      const totalItemHeight =
        (shownType === "detail" ? 68 : 40) * fileList.length;
      if (
        totalHeight > totalItemHeight &&
        appState.dropboxFile?.files &&
        appState.dropboxFile?.files.length === 30
      ) {
        changeNextToken(appState.dropboxFile?.nextPageToken);
      }
    }, 200);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize); // cleanup
    };
  }, [fileList]);
  /** 드라이브 리스트에서 리스트 무한스크롤시 사용 */
  const handleListScroll = (e: any): void => {
    const offsetHeight = e.target.offsetHeight;
    const scrollTop = e.target.scrollTop;
    const scrollHeight = e.target.scrollHeight;

    if (
      offsetHeight &&
      scrollTop &&
      scrollHeight &&
      fileList &&
      Math.ceil(offsetHeight + scrollTop + 2) >= scrollHeight &&
      appState.dropboxFile?.nextPageToken !== nextToken
    ) {
      changeNextToken(appState.dropboxFile?.nextPageToken);
    }
  };

  const renderDropboxItem = (file: DropboxFileInfo): React.ReactNode => {
    return (
      <div
        className={classNames("dropbox-item", shownType, {
          checked: checkboxGroup.isSelected(file.id),
        })}
        id={
          "appState.dropboxFile:" +
          (checkboxGroup.isSelected(file.id)
            ? fileList
                ?.filter((item) => checkboxGroup.isSelected(item.id))
                ?.map((item1: DropboxFileInfo) => {
                  return JSON.stringify({
                    id: item1.id,
                    size: item1.size,
                    name: item1.name,
                  });
                })
                .join(", ")
            : JSON.stringify({
                id: file.id,
                size: file.size,
                name: file.name,
              }))
        }
        draggable={file.size !== undefined}
        onDragStart={onDragStart}
        onClick={() =>
          file.size === undefined
            ? changePaths([
                ...paths,
                { id: file.id, name: file.name, path: file.pathLower },
              ])
            : checkboxGroup.handleClick(file.id)
        }
      >
        {file.size !== undefined ? (
          <Checkbox
            check={checkboxGroup.isSelected(file.id)}
            onClick={() => checkboxGroup.handleClick(file.id)}
          />
        ) : (
          <Box width={"20px"} />
        )}
        {file.size === undefined ? (
          <Icons
            variant="document"
            label="folder"
            size={shownType === "detail" ? 32 : 20}
          />
        ) : (
          utilsDocs.renderDocIcon(file.name, shownType === "detail" ? 32 : 20)
        )}
        <Box direction="column" classes="dropbox-item--body">
          <Tooltip
            message={file.name ?? ""}
            width={"fit-content"}
            maxWidth={"100%"}
          >
            <Typography type="body4_md" ellipsis maxWidth="100%">
              {file.name ?? ""}
            </Typography>
          </Tooltip>
          {shownType === "detail" && (
            <Box classes="mg mt-8" alignItems="center">
              <Typography
                type="body4_rg"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
                whiteSpace="pre"
              >
                {file.size === undefined
                  ? t("common.folder")
                  : utilsDocs.fileSizeFormat(file.size)}
              </Typography>
              {file.serverModified && (
                <>
                  <hr
                    className="divider-col"
                    style={{ margin: "0 8px", height: "12px" }}
                  />
                  <Typography
                    type={"body4_rg"}
                    exactColor={getColor("COLOR_TEXT_SECONDARY")}
                    whiteSpace="pre"
                    ellipsis
                  >
                    {utilsCommon.renderDateFormat(file.serverModified)}
                  </Typography>
                </>
              )}
            </Box>
          )}
        </Box>
      </div>
    );
  };

  return (
    <Box classes="dropbox">
      <Box classes="dropbox-topBar">
        <Box alignItems="center" justifyContent="space-between" width={"100%"}>
          <Button
            btnStyle="icon"
            onClick={() => {
              changeNextToken(undefined);
              getDropboxList({
                folder: paths[paths.length - 1].id,
                order: order,
                isRefresh: true,
                folderPath: currentPath,
              });
            }}
            leadingIcon={<Icons variant="line" label="redoLeft" />}
          />

          <Box classes="dropbox-topBar--menus">
            <Dropdown
              width={80}
              placeholder=" "
              btnStyle="link"
              value={
                shownType === "detail" ? t("common.detail") : t("common.less")
              }
              align="right"
            >
              {[
                { resource: t("common.detail"), key: "detail" },
                { resource: t("common.less"), key: "simple" },
              ].map((item) => (
                <DropdownItem
                  onClick={(e) => changeShownType(item.key)}
                  checkColor={shownType === item.key}
                  checked={shownType === item.key}
                  type="check"
                  label={item.resource}
                />
              ))}
            </Dropdown>
            <Dropdown
              width={90}
              placeholder=" "
              btnStyle="link"
              value={order.replace("desc", "")}
              align="right"
            >
              {[
                { resource: t("common.byName"), key: "file" },
                { resource: t("common.byDate"), key: "date" },
              ].map((item) => (
                <DropdownItem
                  onClick={(e) => changeSortBy(item.key)}
                  checkColor={order.replace("desc", "") === item.key}
                  checked={order.replace("desc", "") === item.key}
                  type="check"
                  label={item.resource}
                />
              ))}
            </Dropdown>
            <Button
              btnStyle="icon"
              leadingIcon={
                <Icons
                  variant="line"
                  label="arrow"
                  direction={order.includes("desc") ? "down" : "up"}
                />
              }
              onClick={() =>
                changeSortBy(
                  order.includes("desc")
                    ? order.replace("desc", "")
                    : order + "desc"
                )
              }
            />
          </Box>
        </Box>
        <hr className="divider" />
      </Box>
      <Box alignItems="center" height={32} classes="dropbox-path">
        <Box classes="mg ml-8" alignItems="center" style={{ maxWidth: 278 }}>
          {paths.map((path, index) => (
            <Box
              alignItems="center"
              key={"dropbox-" + path.id}
              style={{ overflow: index === paths.length - 1 ? "hidden" : "" }}
            >
              {paths.length < 4 ||
              index === 0 ||
              index > paths.length - 3 ||
              index !== 1 ? (
                <Box
                  onClick={() =>
                    (paths.length < 4 || index !== 1) &&
                    index !== paths.length - 1 &&
                    changePaths(
                      paths.filter((path1, index1) => index1 <= index)
                    )
                  }
                  style={{
                    overflow: index === paths.length - 1 ? "hidden" : "",
                  }}
                  classes={classNames({
                    "cursor pointer": paths.length < 4 || index !== 1,
                  })}
                >
                  <Tooltip
                    message={path.name}
                    maxWidth={
                      [0, paths.length - 1].includes(index) ? "none" : 68
                    }
                    width={"-webkit-fill-available"}
                  >
                    <Typography
                      type="body2_rg"
                      maxWidth={
                        [0, paths.length - 1].includes(index) ? "none" : 68
                      }
                      width={"-webkit-fill-available"}
                      exactColor={
                        index === paths.length - 1
                          ? getColor("COLOR_TEXT_PRIMARY")
                          : getColor("COLOR_TEXT_INFO")
                      }
                      fontWeight={700}
                      ellipsis
                    >
                      {paths.length < 4 ||
                      index === 0 ||
                      index > paths.length - 3
                        ? path.name
                        : index === 1
                        ? "..."
                        : ""}
                    </Typography>
                  </Tooltip>
                </Box>
              ) : (
                <Dropdown buttonClick={buttonClick}>
                  {paths
                    .filter(
                      (item, index3) =>
                        index3 !== 0 && index3 < paths.length - 2
                    )
                    .map((path2, index2) => (
                      <DropdownItem
                        onClick={() => {
                          changePaths(
                            paths.filter(
                              (path1, index1) => index1 <= index2 + 1
                            )
                          );
                          setButtonClick(buttonClick + 1);
                        }}
                        label={path2.name}
                      ></DropdownItem>
                    ))}
                </Dropdown>
              )}
              {paths.length !== 1 &&
                [0, paths.length - 2, paths.length - 3].includes(index) && (
                  <Icons
                    variant="line"
                    label="chevron"
                    stroke={"#b5bbc2"}
                    direction="right"
                  />
                )}
            </Box>
          ))}
        </Box>
      </Box>

      {checkboxGroup.selected.length > 0 && (
        <Box height={28} alignItems="center" classes="mg mt-8 ml-12 mb-8">
          <Typography type="subtitle2" lineHeight="20px">
            {t("account.app.selected")}
          </Typography>
          <Typography
            type="subtitle2"
            lineHeight="20px"
            exactColor={getColor("COLOR_TEXT_BRAND")}
            classes="mg ml-4"
          >
            {checkboxGroup.selected.length}
          </Typography>
        </Box>
      )}
      <Box
        classes="dropbox-items"
        direction="column"
        id={"dropboxFiles"}
        height={
          checkboxGroup.selected.length > 0
            ? "calc(100% - 230px)"
            : "calc(100% - 184px)"
        }
        onScroll={handleListScroll}
      >
        {fileList && fileList?.length > 0 ? (
          fileList?.map((item) => renderDropboxItem(item))
        ) : (
          <Box
            direction="column"
            alignItems="center"
            height={"100%"}
            justifyContent="center"
          >
            <img
              src={require("../../../shared/images/pages/File/noFile.png")}
              alt="noContent"
              width={200}
              height={120}
            />
            <Typography
              type="caption_rg"
              lineHeight="18px"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
              classes="mg mt-16"
            >
              {t("account.app.noContent")}
            </Typography>
          </Box>
        )}
      </Box>
      <Box justifyContent="flex-end" classes="dropbox-bottom">
        <Button
          classes="mg mr-28"
          state={checkboxGroup.selected.length === 0 ? "disable" : "default"}
          onClick={() => {
            if (fileList)
              handleUploadFilesInUID(
                checkboxGroup.selected ?? [],
                fileList.map((item) => {
                  return {
                    id: item.id,
                    name: item.name,
                    size: item.size,
                  };
                }),
                "DROPBOX",
                paths[paths.length - 1].path
              );
          }}
          btnStyle="fill"
          label={t("account.app.shareToWorkgroup")}
        ></Button>
      </Box>
    </Box>
  );
};

export default DropboxList;
